import type { FC, ReactNode } from 'react'

import { useState } from 'react'
import { CalendarClock, CircleCheck, FileText, Send } from 'lucide-react'
import { cn } from '@/lib/utils'
import { formatCurrency } from '@/lib/format'
import { FloatingCard, PreviewFrame, Segmented } from '@/components/features/preview-frame'
import { REPORTS } from '@/constants/dashboard'

type ReportKey = keyof typeof REPORTS

const OPTIONS: Array<{ value: ReportKey, label: string }> = [
    { value: 'pnl', label: 'P&L' },
    { value: 'balance', label: 'Balance sheet' },
    { value: 'cashflow', label: 'Cash flow' }
]

const accounting = (value: number): string => {
    return value < 0 ? `(${formatCurrency(Math.abs(value))})` : formatCurrency(value)
}

export const ReportingPreview: FC = (): ReactNode => {
    const [report, setReport] = useState<ReportKey>('pnl')
    const current = REPORTS[report]

    return (
        <div className='relative'>
            <PreviewFrame>
                <div className='flex flex-wrap items-start justify-between gap-3'>
                    <div className='flex items-center gap-3'>
                        <span className='flex size-9 items-center justify-center rounded-xl bg-burgundy text-gold'>
                            <FileText aria-hidden='true' className='size-4' />
                        </span>

                        <div>
                            <p className='text-sm font-medium'>{current.title}</p>
                            <p className='text-[11px] text-stone'>{current.subtitle}</p>
                        </div>
                    </div>

                    <span className='inline-flex items-center gap-1.5 rounded-full bg-sage/20 px-2.5 py-1 text-[11px] font-medium text-sage-dark'>
                        <CircleCheck aria-hidden='true' className='size-3.5' />
                        Ready for review
                    </span>
                </div>

                <Segmented
                    label='Report type'
                    value={report}
                    options={OPTIONS}
                    onChange={setReport}
                    className='mt-5'
                />

                <table key={report} className='mt-4 w-full animate-in fade-in text-sm duration-500'>
                    <caption className='sr-only'>{current.title}, {current.subtitle}</caption>

                    <tbody>
                        {current.lines.map(line => (
                            <tr
                                key={line.label}
                                className={cn('border-t', line.emphasis ? 'border-burgundy/15' : 'border-burgundy/[0.06]')}
                            >
                                <th scope='row' className={cn('py-2.5 pr-3 text-left font-normal', line.emphasis ? 'font-medium text-burgundy' : 'text-stone-dark')}>
                                    {line.label}
                                </th>

                                <td className={cn('py-2.5 text-right tabular-nums', line.emphasis && 'font-medium')}>
                                    {accounting(line.value)}
                                </td>

                                <td className='hidden w-20 py-2.5 pl-3 text-right text-[11px] tabular-nums text-stone sm:table-cell'>
                                    {line.note ?? ''}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>

                <div className='mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-burgundy/[0.06] pt-4 text-[11px] text-stone'>
                    <span className='inline-flex items-center gap-1.5'>
                        <CalendarClock aria-hidden='true' className='size-3.5' />
                        Generated automatically at 6:02 AM
                    </span>

                    <span className='inline-flex items-center gap-1.5'>
                        <Send aria-hidden='true' className='size-3.5' />
                        Delivered to 4 stakeholders
                    </span>
                </div>
            </PreviewFrame>

            <FloatingCard className='-bottom-8 -left-6 w-56 lg:-left-10'>
                <p className='flex items-center gap-2 text-xs font-medium'>
                    <span className='flex size-6 items-center justify-center rounded-full bg-sage/25 text-sage-dark'>
                        <CircleCheck aria-hidden='true' className='size-3.5' />
                    </span>
                    Board pack delivered
                </p>

                <p className='mt-1.5 text-[11px] text-stone'>PDF and Excel, with AI commentary on 6 variances</p>
            </FloatingCard>

            <FloatingCard slow className='-right-4 -top-6 lg:-right-8'>
                <p className='font-mono text-[10px] uppercase tracking-[0.14em] text-stone'>Schedule</p>
                <p className='mt-1 text-xs font-medium'>Monthly, first business day</p>
            </FloatingCard>
        </div>
    )
}
